<template>
  <div class="mt-2 text-sm text-red-700 dark:text-red-300 flex items-center flex-wrap">
    <span>This may happen if you haven't provided local network access to this site.</span>
    <div
      ref="tooltipTrigger"
      class="relative ml-1"
      @mouseenter="showTooltip"
      @mouseleave="hideTooltip"
    >
      <svg
        class="h-4 w-4 text-red-500 dark:text-red-400 cursor-help"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <div
        v-show="tooltipVisible"
        :class="[
          'absolute z-50 left-1/2 transform -translate-x-1/2',
          tooltipPosition === 'above' ? 'bottom-full mb-2' : 'top-full mt-2'
        ]"
      >
        <div
          :class="[
            isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
            'border rounded-lg shadow-lg p-2 w-80'
          ]"
        >
          <p class="text-xs text-gray-600 dark:text-gray-300 mb-2">
            When prompted, click "Allow" to grant local network access:
          </p>
          <img
            src="@/assets/images/local-network-access-prompt.png"
            alt="Local Network Access Permission Dialog"
            class="w-full rounded border border-gray-200 dark:border-gray-600"
          />
        </div>
        <!-- Arrow pointing down (when tooltip is above) -->
        <div
          v-if="tooltipPosition === 'above'"
          :class="[
            isDark ? 'border-t-gray-800' : 'border-t-white',
            'absolute left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent'
          ]"
        ></div>
        <!-- Arrow pointing up (when tooltip is below) -->
        <div
          v-if="tooltipPosition === 'below'"
          :class="[
            isDark ? 'border-b-gray-800' : 'border-b-white',
            'absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-full w-0 h-0 border-l-8 border-r-8 border-b-8 border-l-transparent border-r-transparent'
          ]"
        ></div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, inject, computed } from 'vue'
import { useTheme } from '../../utils/useTheme'

const theme = inject('theme', useTheme())
const isDark = computed(() => theme.isDark.value)

// Tooltip state
const tooltipTrigger = ref<HTMLElement | null>(null)
const tooltipVisible = ref(false)
const tooltipPosition = ref<'above' | 'below'>('below')

const TOOLTIP_HEIGHT = 280 // Approximate height of the tooltip

const showTooltip = () => {
  if (tooltipTrigger.value) {
    const rect = tooltipTrigger.value.getBoundingClientRect()
    const spaceAbove = rect.top
    const spaceBelow = window.innerHeight - rect.bottom

    // Prefer showing below, but show above if not enough space below and more space above
    if (spaceBelow < TOOLTIP_HEIGHT && spaceAbove > spaceBelow) {
      tooltipPosition.value = 'above'
    } else {
      tooltipPosition.value = 'below'
    }
  }
  tooltipVisible.value = true
}

const hideTooltip = () => {
  tooltipVisible.value = false
}
</script>

